'use client';

import { Box } from '@terra/ui-styled-system';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { CopilotComposer } from '../editor/lexical';
import type { ImperativeEditorRef } from '../editor/lexical';
import type { MentionEntityWithOffset } from '../editor/lexical/plugins/buildMessageSegments';
import {
  getMentionEntitiesFromUserMessage,
  getMentionSegmentsFromUserMessage,
  getUserMessageDisplayText,
} from '../utils/userMessageDisplayText';
import { UserMessageCardAttachments } from './UserMessageCardAttachments';

const FLOATING_LAYER_SELECTOR =
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-scope="popover"]';

export type InlineUserMessageEditSubmit = {
  text: string;
  positionedMentions: MentionEntityWithOffset[];
};

export type InlineUserMessageEditorProps = {
  uiMessage: TerraUIMessage;
  editorRef?: (instance: ImperativeEditorRef | null) => void;
  onSubmit: (edit: InlineUserMessageEditSubmit) => void;
  onCancel: () => void;
  onMentionsTrigger?: (position?: { x: number; y: number }) => void;
  isSubmitting?: boolean;
  isCancelBlocked?: boolean;
};

export const InlineUserMessageEditor = memo(function InlineUserMessageEditor({
  uiMessage,
  editorRef,
  onSubmit,
  onCancel,
  onMentionsTrigger,
  isSubmitting = false,
  isCancelBlocked = false,
}: InlineUserMessageEditorProps) {
  const t = useTranslations('designSystem.chat.branching');
  const containerRef = useRef<HTMLDivElement>(null);
  const localEditorRef = useRef<ImperativeEditorRef | null>(null);
  const isCancelBlockedRef = useRef(isCancelBlocked);
  const onCancelRef = useRef(onCancel);

  isCancelBlockedRef.current = isCancelBlocked || isSubmitting;
  onCancelRef.current = onCancel;

  const prefillEditor = useCallback(
    (instance: ImperativeEditorRef) => {
      const segments = getMentionSegmentsFromUserMessage({
        message: uiMessage,
      });

      if (segments?.some((segment) => segment.type === 'mention')) {
        instance.setContentFromMessageSegments({
          segments,
          mentions: getMentionEntitiesFromUserMessage({ message: uiMessage }),
        });
      } else {
        instance.setContent(getUserMessageDisplayText({ message: uiMessage }));
      }

      instance.focus();
    },
    [uiMessage],
  );

  const handleEditorRef = useCallback(
    (instance: ImperativeEditorRef | null) => {
      const isNewInstance = instance && instance !== localEditorRef.current;

      localEditorRef.current = instance;
      editorRef?.(instance);

      if (isNewInstance) {
        requestAnimationFrame(() => prefillEditor(instance));
      }
    },
    [editorRef, prefillEditor],
  );

  useEffect(() => () => editorRef?.(null), [editorRef]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Element) || isCancelBlockedRef.current) {
        return;
      }

      if (
        containerRef.current?.contains(target) ||
        target.closest(FLOATING_LAYER_SELECTOR)
      ) {
        return;
      }

      onCancelRef.current();
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const handleKeyDownCapture = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape' || isCancelBlockedRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onCancel();
  };

  const handleSubmit = ({ content }: { content: string }) => {
    const instance = localEditorRef.current;
    const positionedMentions = instance?.getMentions() ?? [];
    const editorState = instance?.getEditorState();

    onSubmit({ text: content, positionedMentions });

    if (instance && editorState) {
      setTimeout(() => instance.setEditorState(editorState), 0);
    }
  };

  return (
    <Box
      ref={containerRef}
      width="full"
      onKeyDownCapture={handleKeyDownCapture}
    >
      <CopilotComposer
        ref={handleEditorRef}
        onSubmit={handleSubmit}
        onStop={onCancel}
        onMentionsTrigger={onMentionsTrigger}
        isSubmitDisabled={isSubmitting}
        submitButtonAriaLabel={t('submitEdit')}
        placeholder={t('editPlaceholder')}
        composerAttachments={<UserMessageCardAttachments message={uiMessage} />}
      />
    </Box>
  );
});
